import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { auth as authApi, configureApiClient, type AuthUser } from '../lib/api.js';

interface AuthState {
  accessToken: string | null;
  user: AuthUser | null;
  currentHomeId: string | null;
  isLoading: boolean;
}

interface AuthContextValue extends AuthState {
  setAuth: (token: string, user: AuthUser) => void;
  setUser: (user: AuthUser) => void;
  logout: () => Promise<void>;
  getToken: () => string | null;
  refresh: () => Promise<string | null>;
  setCurrentHomeId: (id: string) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    accessToken: null,
    user: null,
    currentHomeId: null,
    isLoading: true,
  });

  const tokenRef = useRef<string | null>(null);

  const getToken = useCallback(() => tokenRef.current, []);

  const refresh = useCallback(async (): Promise<string | null> => {
    try {
      const result = await authApi.refresh();
      tokenRef.current = result.accessToken;
      setState((prev) => ({
        ...prev,
        accessToken: result.accessToken,
        user: result.user,
        currentHomeId: prev.currentHomeId ?? result.user.primaryHomeId ?? null,
      }));
      return result.accessToken;
    } catch {
      tokenRef.current = null;
      setState((prev) => ({ ...prev, accessToken: null, user: null, isLoading: false }));
      return null;
    }
  }, []);

  useEffect(() => {
    configureApiClient(getToken, refresh);
  }, [getToken, refresh]);

  useEffect(() => {
    refresh().finally(() => {
      setState((prev) => ({ ...prev, isLoading: false }));
    });
  }, [refresh]);

  useEffect(() => {
    const INTERVAL = 10 * 60 * 1000;
    const interval = setInterval(() => {
      if (tokenRef.current) void refresh();
    }, INTERVAL);
    const onFocus = () => {
      if (tokenRef.current) void refresh();
    };
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
    };
  }, [refresh]);

  const setAuth = useCallback((token: string, user: AuthUser) => {
    tokenRef.current = token;
    setState((prev) => ({
      ...prev,
      accessToken: token,
      user,
      currentHomeId: prev.currentHomeId ?? user.primaryHomeId ?? null,
    }));
  }, []);

  const setUser = useCallback((user: AuthUser) => {
    setState((prev) => ({ ...prev, user }));
  }, []);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch {
      /* ignore */
    }
    tokenRef.current = null;
    setState({ accessToken: null, user: null, currentHomeId: null, isLoading: false });
  }, []);

  const setCurrentHomeId = useCallback((id: string) => {
    setState((prev) => ({ ...prev, currentHomeId: id }));
  }, []);

  return (
    <AuthContext.Provider
      value={{ ...state, setAuth, setUser, logout, getToken, refresh, setCurrentHomeId }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
